import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { min } from 'date-fns'
import React, { FC, Fragment, useEffect, useState } from 'react'
import { Button, OverlayTrigger, Spinner, Tab, Tabs, Tooltip } from 'react-bootstrap'
import DataTable from 'react-data-table-component'
import { toast } from 'react-toastify'
import { getUploadDataProductAsync, uploadProductCsvAsync } from 'src/redux/actions/omsProductActions'
import { useAppDispatch } from 'src/redux/storeHooks'
import { formatDateLocal, formatTimeZone } from 'src/utils/TimeZoneDateFormat'
import StatusButton from '../orders/StatusButton'
import { extractAPIErrorMessage } from 'src/utils/extractAPIErrorMessage'
import TableLoadingShell from 'src/utils/TableLoadingShell'

type UploadRow = {
    id: number
    identifier?: string | null
    sync_status?: string
    created_at?: string
    updated_at?: string
    fileUrl: string
    message?: string
    feature?: string
    filename?: string
}

type Props = {
    show: boolean
    isActive?: boolean
    handleClose: () => void
    platformId: string | undefined
    connectedPlatformTypes?: Record<string, any> | null
    onBusyChange?: (busy: boolean) => void
}

const UploadCsvProduct: FC<Props> = ({
    show,
    isActive,
    handleClose,
    platformId,
    connectedPlatformTypes,
    onBusyChange,
}) => {
    const dispatch = useAppDispatch()
    const isTabActive = isActive ?? show

    const [file, setFile] = useState<File | null>(null)
    const [invalidFileType, setInvalidFileType] = useState(false)
    const [loadingList, setLoadingList] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [uploadList, setUploadList] = useState<UploadRow[]>([])
    const [uploadFeature, setUploadFeature] = useState('IMPORT_PRODUCTS')
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [totalRows, setTotalRows] = useState(0)

    const normalizedPlatformName = String(connectedPlatformTypes?.self_data?.name ?? '')
        .replace(/\s+/g, '')
        .toUpperCase()
    const isAmazonVendor = normalizedPlatformName === 'AMAZONVENDOR'

    const templateUrl = React.useMemo(() => {
        const files = connectedPlatformTypes?.sample_product_files
        if (!Array.isArray(files)) return ''
        const found = files.find((f: any) => String(f?.label ?? '').toLowerCase() === 'oms_store')
        return found?.sample_url ?? ''
    }, [connectedPlatformTypes])

    const templateLabel = React.useMemo(() => {
        const files = connectedPlatformTypes?.sample_product_files
        if (!Array.isArray(files)) return ''
        const found = files.find((f: any) => String(f?.label ?? '').toLowerCase() === 'oms_store')
        const rawLabel = String(found?.label ?? '').trim()
        if (!rawLabel || rawLabel.toLowerCase() === 'oms_store') return 'Commercium OMS'
        return rawLabel
    }, [connectedPlatformTypes])

    const maxUploadSize = React.useMemo(() => {
        const files = connectedPlatformTypes?.sample_product_files
        if (!Array.isArray(files)) return ''
        const found = files.find((f: any) => String(f?.label ?? '').toLowerCase() === 'oms_store')
        return found?.max_upload_size ?? ''
    }, [connectedPlatformTypes])

    const customStyles = {
        tableWrapper: {
            style: {
                border: '1px solid gainsboro',
                borderRadius: '8px',
                overflow: 'hidden',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid gainsboro',
                minHeight: '44px',
            },
        },
        headCells: {
            style: {
                fontWeight: 700,
                fontSize: '13px',
            },
        },
        rows: {
            style: {
                minHeight: '48px',
            },
        },
        cells: {
            style: {
                paddingTop: '10px',
                paddingBottom: '10px',
                borderBottom: '1px solid gainsboro',
                fontSize: '13px',
            },
        },
        pagination: {
            style: {
                minHeight: '10px', // this targets rdt_Pagination
            },
        },
    }

    const fetchHistory = async () => {
        try {
            if (!platformId) return
            setLoadingList(true)

            const res = await dispatch(getUploadDataProductAsync({ id: Number(platformId), pag: page, limit: limit }))
            console.log('Upload history response:', res) // Debug log

            const payload = res.payload as any
            const list = payload?.results?.data
            setUploadList(Array.isArray(list) ? list : [])
            setTotalRows(payload?.count ?? 0)

        } catch (err) {
            toast.error('Failed to fetch upload history')
        } finally {
            setLoadingList(false)
        }
    }

    useEffect(() => {
        if (isTabActive) {
            fetchHistory()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTabActive, platformId, page, limit])

    useEffect(() => {
        if (show) {
            setUploadFeature('IMPORT_PRODUCTS')
        } else {
            setFile(null)
            setInvalidFileType(false)
            setPage(1)
            setLimit(10)
        }
    }, [show])

    useEffect(() => {
        onBusyChange?.(uploading || loadingList)
    }, [uploading, loadingList, onBusyChange])

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0]
        if (!selectedFile) return

        const ext = selectedFile.name.split('.').pop()?.toLowerCase()
        const allowedExtensions =
            uploadFeature === 'IMPORT_PRODUCTS_PO' ? ['csv', 'xlsx', 'xlsm'] : ['csv']
        if (!ext || !allowedExtensions.includes(ext)) {
            setInvalidFileType(true)
            setFile(null)
            event.target.value = ''
            return
        }

        setInvalidFileType(false)
        setFile(selectedFile)
    }

    const handleUpload = async () => {
        if (!file) {
            toast.warning('Please choose a file to upload')
            return
        }
        if (!platformId) {
            toast.warning('Platform id missing')
            return
        }

        try {
            setUploading(true)
            const formData = new FormData()
            formData.append('platform_id', String(platformId))
            formData.append('file', file)
            if (uploadFeature === 'IMPORT_PRODUCTS_PO') {
                formData.append('feature', 'IMPORT_PRODUCTS_PO')
            }

            const res = await dispatch(uploadProductCsvAsync(formData))
            const payload = res.payload as any
            const status = payload?.status ?? res?.meta?.requestStatus
            const isSuccess = payload?.status === 200 || payload?.status === 201

            if (isSuccess) {
                toast.success(payload?.message ?? 'File uploaded successfully!')
                setFile(null)
                setInvalidFileType(false)
                fetchHistory()
                handleClose()
                return;
            }

            const errorMessage = extractAPIErrorMessage(payload, 'Upload failed')

            toast.error(errorMessage)
        } catch (e: any) {
            const errorMessage = extractAPIErrorMessage(e, 'Upload failed')
            toast.error(errorMessage)
        } finally {
            setUploading(false)
        }
    }

    const columns = [
        {
            name: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span>File Name</span>
                    <OverlayTrigger placement="top" overlay={<Tooltip>File Name</Tooltip>}>
                        <span style={{ cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
           cell: (row: UploadRow) => {
    const filename = row.filename ?? '-';
    const shortFilename =
        filename.length > 15 ? `${filename.slice(0, 15)}...` : filename;

    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <Tooltip id={`tooltip-feature-${row.id}`}>
                    {filename}
                </Tooltip>
            }
        >
            <span style={{ cursor: 'pointer' }}>
                {shortFilename}
            </span>
        </OverlayTrigger>
    );
},
            width: '200px',
        },

        {
            name: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span>Upload Status</span>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Upload Status</Tooltip>}>
                        <span style={{ cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            cell: (row: UploadRow) => (
                <StatusButton
                    syncStatus={row.sync_status ?? 'PENDING'}
                    syncMessage={row.message}
                />
            ),
            width: '170px',
            center: true,
        },

        // {
        //     name: (
        //         <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        //             <span>Message</span>
        //             <OverlayTrigger placement="top" overlay={<Tooltip>Message</Tooltip>}>
        //                 <span style={{ cursor: 'pointer' }}>
        //                     <FontAwesomeIcon icon={faInfoCircle} />
        //                 </span>
        //             </OverlayTrigger>
        //         </div>
        //     ),
        //     cell: (row: UploadRow) => {
        //         const message = row.message ?? '-';
        //         const shortMessage =
        //             message.length > 30 ? `${message.slice(0, 30)}...` : message;

        //         return (
        //             <OverlayTrigger
        //                 placement="top"
        //                 overlay={
        //                     <Tooltip id={`tooltip-message-${row.id}`}>
        //                         {message}
        //                     </Tooltip>
        //                 }
        //             >
        //                 <span style={{ cursor: 'pointer' }}>
        //                     {shortMessage}
        //                 </span>
        //             </OverlayTrigger>
        //         );
        //     },
        //     width: '250px',
        // },

        {
            name: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span>Created At</span>
                    <OverlayTrigger placement="top" overlay={<Tooltip>{formatTimeZone()}</Tooltip>}>
                        <span style={{ cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            cell: (row: UploadRow) => (
                <p style={{ margin: 0 }}>
                    {row.created_at ? formatDateLocal(row.created_at) : ''}
                </p>
            ),
            right: true,
            width: '170px',
            center: true,
        },

        {
            name: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span>Updated At</span>
                    <OverlayTrigger placement="top" overlay={<Tooltip>{formatTimeZone()}</Tooltip>}>
                        <span style={{ cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            cell: (row: UploadRow) => (
                <p style={{ margin: 0 }}>
                    {row.updated_at ? formatDateLocal(row.updated_at) : ''}
                </p>
            ),
            right: true,
            width: '170px',
            center: true,
        },

        {
            name: 'Action',
            cell: (row: UploadRow) => (
                <div className="d-flex justify-content-center w-100">
                    <a 
                        href={row.fileUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        download
                        title="Download"
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center upload-csv-download-btn"
                        style={{ width: '32px', height: '32px', border: 'none' }}
                    >
                        <i className="bi bi-cloud-arrow-down fs-5"></i>
                    </a>
                </div>
            ),
            width: '100px',
            center: true,
        },
    ];

    return (
        <Fragment>
            <style>
                {`
                .upload-csv-download-btn:hover i {
                    color: #ffffff !important;
                }
                `}
            </style>
            {isAmazonVendor && (
                <Tabs
                    activeKey={uploadFeature}
                    className="mb-3"
                    transition={false}
                    onSelect={(ev) => {
                        if (!ev) return
                        setUploadFeature(ev)
                        setFile(null)
                        setInvalidFileType(false)
                    }}
                >
                    <Tab eventKey="IMPORT_PRODUCTS" title="Upload Direct Fulfilment Products" />
                    <Tab eventKey="IMPORT_PRODUCTS_PO" title="Upload Retail Products" />
                </Tabs>
            )}

            {/* Top toolbar row: Template download + Upload */}
            <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
                        <div className="d-flex align-items-end gap-8">
                        {/* RIGHT: Upload control */}
                        <div className="d-flex flex-column" style={{ minWidth: 320 }}>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                Upload your product file
                                </span>
                                <span className={`small ${invalidFileType ? 'text-danger' : 'text-muted'}`} style={{ fontSize: 11 }}>
                                    Accepted: {uploadFeature === 'IMPORT_PRODUCTS_PO' ? '.csv, .xlsx, .xlsm' : '.csv'}
                                </span>
                            </div>

                            {/* Input-like box */}
                            <div
                                className="d-flex align-items-center justify-content-between gap-2"
                                    style={{
                                        border: `1px solid ${invalidFileType ? '#dc3545' : '#dee2e6'}`,
                                        borderRadius: 10,
                                        padding: '6px 8px',
                                        background: '#fff',
                                    }}
                                >
                                <label
                                    htmlFor="product-upload"
                                    className="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    <i className="bi bi-upload me-2"></i>
                                    Choose file
                                </label>

                                {/* File chip */}
                                <div className="d-flex align-items-center gap-2 flex-grow-1 justify-content-end" style={{ minWidth: 0 }}>
                                    {file ? (
                                        <span
                                            className="d-inline-flex align-items-center"
                                            title={file.name}
                                            style={{
                                                maxWidth: 260,
                                                padding: '4px 10px',
                                                borderRadius: 999,
                                                border: '1px solid #dee2e6',
                                                background: '#f8f9fa',
                                                fontSize: 12,
                                                minWidth: 0,
                                            }}
                                        >
                                            <i className="bi bi-file-earmark-text me-2"></i>
                                            <span className="text-truncate" style={{ maxWidth: 190 }}>
                                                {file.name}
                                            </span>

                                            {/* Optional: clear file */}
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 ms-2 text-muted"
                                                style={{ lineHeight: 1, textDecoration: 'none' }}
                                                title="Remove file"
                                                onClick={() => {
                                                    setFile(null)
                                                    setInvalidFileType(false)
                                                    const input = document.getElementById('product-upload') as HTMLInputElement | null
                                                    if (input) input.value = ''
                                                }}
                                            >
                                                <i className="bi bi-x-lg"></i>
                                            </button>
                                        </span>
                                    ) : (
                                        <span className="text-muted text-truncate" style={{ fontSize: 12, maxWidth: 260 }}>
                                            No file selected
                                        </span>
                                    )}
                                </div>

                                <input
                                    id="product-upload"
                                    type="file"
                                    className="d-none"
                                    accept={uploadFeature === 'IMPORT_PRODUCTS_PO' ? '.csv,.xlsx,.xlsm' : '.csv'}
                                    onChange={handleFileChange}
                                />
                            </div>
                        </div>

                        {/* Moved Upload Button */}
                        <Button 
                            variant="primary" 
                            size="sm" 
                            disabled={uploading || !file} 
                            onClick={handleUpload}
                            style={{ whiteSpace: 'nowrap', height: '45px' }}
                        >
                            {uploading ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    Uploading...
                                </>
                            ) : (
                                'Upload'
                            )}
                        </Button>
                        </div>
                        {/* LEFT: Template download */}
                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 260 }}>
                            <span className="text-muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                Download template for <span className="fw-semibold text-dark">{templateLabel || 'Commercium OMS'}</span>
                                {maxUploadSize ? ` (Max: ${maxUploadSize})` : ''}:
                            </span>

                            {templateUrl ? (
                                <a
                                    href={templateUrl}
                                    className="d-inline-flex align-items-center justify-content-center"
                                    rel="noopener noreferrer"
                                    title="Download template"
                                    download
                                    style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 8,
                                        border: '1px solid #dee2e6',
                                        background: '#fff',
                                        textDecoration: 'none',
                                    }}
                                >
                                    <i className="bi bi-cloud-arrow-down text-primary fs-5"></i>
                                </a>
                            ) : (
                                <span className="badge text-bg-light border" style={{ fontSize: 11 }}>
                                    Not available
                                </span>
                            )}
                        </div>

                    </div>

            <h5 className="mb-3 fw-bold text-dark">Upload History</h5>
            <div style={{ height: '40vh' }}>
                <TableLoadingShell loading={loadingList} minHeight="30vh">
                <DataTable
                    title=""
                    columns={columns as any}
                    data={uploadList}
                    progressPending={false}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={(newPage) => setPage(newPage)}
                    onChangeRowsPerPage={(newPerPage) => {
                        setLimit(newPerPage)
                        setPage(1)
                    }}
                    fixedHeader
                    fixedHeaderScrollHeight="30vh"
                    customStyles={customStyles}
                />
                </TableLoadingShell>
            </div>
        </Fragment>
    )
}

export default UploadCsvProduct
